import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { ReactNode, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { canCreateUsers } from '@/auth/roles.js';
import { useSession } from '@/auth/useSession.js';
import { InfoPage } from '@/components/info-page.js';
import { SchoolNameDivider } from '@/components/students/school-name-divider.js';
import { StudentBulkUpdate } from '@/components/students/student-bulk-update.js';
import { StudentStatusBadge } from '@/components/students/student-status-badge.js';
import {
  EntityTable,
  EntityTableGroupBy,
  EntityTableInfiniteScroll,
  createTableRowSelectionCell,
} from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';

import { EmptyArray, getStudentsNoFutureSessions } from '@/lib/api-calls.js';
import { useInfiniteScrollQuery } from '@/lib/infinite-query-utils.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import {
  DEFAULT_PAGE_SIZE,
  INFINITE_SCROLL_PAGE_SIZE,
  pageSizeSchemaWithMax,
  paginationRequestSchema,
} from '@/lib/pagination.js';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing.js';
import { getMostRecentSession, isAirAdmin } from '@airreading/common';

import { useAllSchools, useOrgOptions, useSchoolAndOrgOptions } from '@/lib/api-call-hooks.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { getDistrictColumn } from '@/lib/district-utils.js';
import { useErrorToast } from '@/lib/error-handling.js';
import {
  calculateAttendanceRate,
  getStudentYearOptions,
  getTotalFutureSessionsFilterOptions,
} from '@/lib/student-utils.js';
import { SchoolNameGroupKey, groupBySchoolName } from '@/lib/table-grouping-utils.js';
import { airColumnHelper } from '@/lib/tables.js';

import { useTimeZone } from '@/auth/useTimeZone.js';
import { useSorting } from '@/lib/sorting.js';
import { CreateStudent } from '@/routes/_layout/users/-components/create-student.js';
import { CreateStudentsByCsv } from '@/routes/_layout/users/-components/create-students-by-csv.js';
import { formatInstant } from '@airreading/common';
import {
  LARGE_MAX_PAGE_SIZE,
  StudentNoFutureSessionsRow,
  GetStudentsSortableColumn,
  StudentStatus,
  StudentYearColorMap,
  getStudentsSortOrderSchema,
  getStudentsSortableColumnSchema,
  studentStatusSchema,
  studentYearOption,
  studentYearSchema,
} from '@airreading/common/data';
import { useFeatureFlag } from '@airreading/feature-flags/web';
import { studentsTabs } from '../-components/tabs.js';

const NO_FUTURE_SESSIONS_DEFAULT_PAGE_SIZE = 100;
const NO_FUTURE_SESSIONS_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50, 100, LARGE_MAX_PAGE_SIZE];

const studentsSearchSchema = paginationRequestSchema.extend({
  pageSize: pageSizeSchemaWithMax(LARGE_MAX_PAGE_SIZE, NO_FUTURE_SESSIONS_DEFAULT_PAGE_SIZE),
  query: z.string().optional().catch(''),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
  statuses: z.array(studentStatusSchema).optional(),
  totalFutureSessions: z.array(z.number()).optional(),
  sortBy: getStudentsSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/students/no-future-sessions/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: StudentsIndex,
  validateSearch: studentsSearchSchema,
});

const col = airColumnHelper<StudentNoFutureSessionsRow>(getStudentsSortableColumnSchema);

const buildColumns = (timeZone: string, schoolIdToDistrictName: Record<string, string>) => [
  createTableRowSelectionCell(col),
  col.accessor((row) => row.id, {
    id: 'fullNameOrEmail',
    header: 'Student Name',
    cell: ({ row, getValue }) => (
      <Link
        to={'/students/$studentId'}
        params={{ studentId: getValue() }}
        search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
      >
        {row.original.fullNameOrEmail}
      </Link>
    ),
  }),
  col.accessor('email', {
    id: 'email',
    header: 'Email',
  }),
  col.accessor('school', {
    id: 'district',
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      const districtName = school?.id ? schoolIdToDistrictName[school.id] : null;
      if (!districtName) return <span>Unknown</span>;
      return school
        ? getDistrictColumn({
            orgId: school.orgId,
            orgName: districtName,
          })
        : null;
    },
  }),
  col.accessor((row) => row, {
    id: 'school',
    header: 'School',
    cell: ({ getValue }) => {
      const school = getValue()?.school;
      return (
        school && (
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name}
          </Link>
        )
      );
    },
  }),
  col.accessor('grade', {
    id: 'grade',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grade = getValue();
      if (!grade) return studentYearOption(grade);
      const backgroundColor = StudentYearColorMap[grade];
      return (
        <div className="min-w-[80px] flex justify-start">
          <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
            {studentYearOption(grade)}
          </span>
        </div>
      );
    },
  }),
  col.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      return (
        <div className="flex gap-x-1">
          <StudentStatusBadge status={getValue()} />
        </div>
      );
    },
  }),
  col.accessor('studentSessions', {
    id: 'mostRecentSessionDate',
    header: 'Most recent session date',
    cell: ({ getValue }) => {
      const latestSession = getMostRecentSession(getValue() ?? [], timeZone);
      return latestSession && formatInstant(latestSession.startInstant, timeZone).formattedDate;
    },
  }),
  col.accessor('learningPath', {
    id: 'learning-path',
    header: 'Learning Path',
    cell: ({ getValue }) => {
      const learningPath = getValue();

      return learningPath?.name;
    },
  }),
  col.accessor('startingLesson', {
    id: 'starting-lesson',
    header: 'Starting lesson',
    cell: ({ getValue }) => {
      const startingLesson = getValue();
      return startingLesson?.name;
    },
  }),
  col.accessor('studentSessions', {
    id: 'attendance-all-past-sessions-last-14-days',
    header: 'Attendance - past 14 days',
    cell: ({ row }) => {
      const sessionFeedbacks = row.original.sessionFeedbacks;
      return `${calculateAttendanceRate(sessionFeedbacks, 14)}%`;
    },
  }),
  col.accessor('totalFutureSessions', {
    id: 'totalFutureSessions',
    header: 'Total future sessions',
    cell: ({ getValue }) => {
      const count = getValue();
      return count ?? 0;
    },
  }),
];

function useStudentsIndexModel() {
  const { timeZone } = useTimeZone();
  const { data: allSchools } = useAllSchools();

  const schoolIdToDistrictName = useMemo(
    () => Object.fromEntries(allSchools.map((school) => [school.id, school.org.name])),
    [allSchools],
  );

  const columns = useMemo(() => buildColumns(timeZone, schoolIdToDistrictName), [timeZone, schoolIdToDistrictName]);
  const [sortByFromSearch, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  // Default sort for this table: by school name ascending
  const effectiveSortBy = sortByFromSearch ?? ['school-asc'];
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentsSortableColumn>([effectiveSortBy, didChangeSortBy]);

  const isSortingBySchool = sorting.some((s) => s.id === 'school');

  const schoolGroupBy = useMemo<EntityTableGroupBy<StudentNoFutureSessionsRow, SchoolNameGroupKey> | undefined>(
    () =>
      isSortingBySchool
        ? {
            getGroupKey: (row) => groupBySchoolName(row.school?.name),
            renderDivider: (groupKey, rowCount) => <SchoolNameDivider groupKey={groupKey} rowCount={rowCount} />,
          }
        : undefined,
    [isSortingBySchool],
  );

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userNameQuery, activeUserNameQuery], setUserNameQuery],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      totalFutureSessions: [
        [selectedTotalFutureSessions, activeSelectedTotalFutureSessions],
        setSelectedTotalFutureSessions,
      ],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'schools', 'orgs', 'grades', 'totalFutureSessions']);

  const allOrganizationOptions = useOrgOptions();
  const allSchoolOptions = useSchoolAndOrgOptions({
    orgIds: activeSelectedOrgs ?? [],
  });

  const requestQuery = useCallback(
    (nextPageIndex: number, nextPageSize: number) =>
      getStudentsNoFutureSessions({
        pageIndex: nextPageIndex,
        pageSize: nextPageSize,
        filters: {
          query: activeUserNameQuery,
          schools: activeSelectedSchools,
          orgs: activeSelectedOrgs,
          grades: activeSelectedGrades,
          totalFutureSessions:
            activeSelectedTotalFutureSessions && activeSelectedTotalFutureSessions.length > 0
              ? activeSelectedTotalFutureSessions
              : undefined,
        },
        sortBy: sortByFromSearch ?? ['school-asc'],
      }),
    [
      activeUserNameQuery,
      activeSelectedSchools,
      activeSelectedOrgs,
      activeSelectedGrades,
      activeSelectedTotalFutureSessions,
      sortByFromSearch,
    ],
  );

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search users by name or email"
          value={userNameQuery ?? ''}
          onChange={(event) => {
            setUserNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Districts"
          options={allOrganizationOptions}
          selectedValues={selectedOrgs ?? []}
          onChange={setSelectedOrgs}
        />
        <TableFilterDropdown
          title="Schools"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterDropdown
          title="Grades"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterDropdown
          title="Total future sessions"
          options={getTotalFutureSessionsFilterOptions()}
          selectedValues={selectedTotalFutureSessions ?? []}
          onChange={setSelectedTotalFutureSessions}
        />

        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      userNameQuery,
      setUserNameQuery,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      allOrganizationOptions,
      selectedOrgs,
      setSelectedOrgs,
      selectedGrades,
      setSelectedGrades,
      selectedTotalFutureSessions,
      setSelectedTotalFutureSessions,
      resetFilters,
    ],
  );

  return {
    columns,
    sorting,
    tableSortingOptions,
    schoolGroupBy,
    pageIndex,
    pageSize,
    onPaginationChange,
    requestQuery,
    filterLeftComponents,
  };
}

type StudentsIndexModel = ReturnType<typeof useStudentsIndexModel>;

function StudentsIndexView({
  model,
  students,
  rowCount,
  isLoading,
  hasError,
  refetch,
  paginationState,
  infiniteScroll,
}: {
  model: StudentsIndexModel;
  students: StudentNoFutureSessionsRow[];
  rowCount: number;
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
  paginationState?: {
    pageIndex: number;
    pageSize: number;
    onPaginationChange: StudentsIndexModel['onPaginationChange'];
  };
  infiniteScroll?: EntityTableInfiniteScroll;
}): ReactNode {
  const { actingUser } = useSession();

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<StudentNoFutureSessionsRow>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => students.find((student) => student.id === id),
    });

  const onSuccess = () => {
    resetAllRowSelection();
    refetch();
  };

  const table = useReactTable({
    data: students,
    columns: model.columns,
    state: {
      ...(paginationState
        ? { pagination: { pageIndex: paginationState.pageIndex, pageSize: paginationState.pageSize } }
        : {}),
      sorting: model.sorting,
      rowSelection,
    },
    rowCount,
    onRowSelectionChange,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    ...(paginationState ? { onPaginationChange: paginationState.onPaginationChange } : {}),
    manualPagination: true,
    ...model.tableSortingOptions,
    enableColumnResizing: true,
  });

  const bulkActionsMenuItems = (
    <>
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to drop"
        invalidMessage="Only enrolled students can be selected."
        options={{ status: StudentStatus.dropped }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark drop off"
        validation={(student) => student.status === StudentStatus.enrolled}
      />
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to conclude the program for"
        invalidMessage="Only students with no program concluded status can be selected."
        options={{ status: StudentStatus.program_concluded }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark program concluded"
        validation={(student) => student.status !== StudentStatus.program_concluded}
      />
    </>
  );

  const topBarRightComponents = canCreateUsers(actingUser) && (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto hidden h-8 lg:flex">
          Add students
          <ChevronDown className="mr-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        <CreateStudent
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add a student</DropdownMenuItem>}
        />
        <CreateStudentsByCsv
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <InfoPage headerText="Students" tabs={studentsTabs('No Future session')}>
      <EntityTable
        table={table}
        showPagination={!infiniteScroll}
        paginationOptions={paginationState ? NO_FUTURE_SESSIONS_PAGE_SIZE_OPTIONS : undefined}
        isLoading={isLoading}
        autoManagedColumnIds={['selection']}
        hasError={hasError}
        emptyState={'No students found'}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents: model.filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        topBarRightComponents={topBarRightComponents}
        groupBy={model.schoolGroupBy}
        infiniteScroll={infiniteScroll}
      />
    </InfoPage>
  );
}

function PagedStudentsIndex() {
  const model = useStudentsIndexModel();
  const { data: response, isPending, error, refetch } = useQuery(model.requestQuery(model.pageIndex, model.pageSize));
  useErrorToast(error, 'Error loading students. Please try again.');
  const students = useMemo(() => response?.students ?? EmptyArray, [response]);

  return (
    <StudentsIndexView
      model={model}
      students={students}
      rowCount={response?.total || 0}
      isLoading={isPending}
      hasError={!!error}
      refetch={refetch}
      paginationState={{
        pageIndex: model.pageIndex,
        pageSize: model.pageSize,
        onPaginationChange: model.onPaginationChange,
      }}
    />
  );
}

function InfiniteStudentsIndex() {
  const model = useStudentsIndexModel();
  const {
    data: response,
    isPending,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteScrollQuery(model.requestQuery, (result) => result.students, INFINITE_SCROLL_PAGE_SIZE);
  useErrorToast(error, 'Error loading students. Please try again.');
  const students = useMemo(() => response?.pages.flatMap((page) => page.students) ?? EmptyArray, [response]);

  return (
    <StudentsIndexView
      model={model}
      students={students}
      rowCount={response?.pages[0]?.total || 0}
      isLoading={isPending}
      hasError={!!error}
      refetch={refetch}
      infiniteScroll={{
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        pageSize: INFINITE_SCROLL_PAGE_SIZE,
      }}
    />
  );
}

function StudentsIndex() {
  const infiniteScrollEnabled = useFeatureFlag('entity-table-infinite-scroll', false);
  return infiniteScrollEnabled ? <InfiniteStudentsIndex /> : <PagedStudentsIndex />;
}
